import type {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { Controller } from "react-hook-form";

import MultiSelect from "./MultiSelect";
import type { OptionType } from "./Select";

type ControlledMultiSelectProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string | null;
  errors: FieldErrors<T>;
  onSave: (value: OptionType[]) => Promise<void>;
  readOnly?: boolean;
  required?: boolean;
  maxLength?: number;
  classNames?: string;
  options: OptionType[];
  placeholder?: string;
  disableClearAll?: boolean;
  disableRemoveCondition?: boolean;
  rules?: RegisterOptions<T, Path<T>>;
  widthVariant?: "full" | "medium" | "wide";
};

const ControlledMultiSelect = <T extends FieldValues>({
  name,
  label,
  errors,
  control,
  options = [],
  placeholder,
  onSave,
  readOnly,
  required = true,
  rules,
  disableClearAll = false,
  disableRemoveCondition = true,
  widthVariant = "full",
}: ControlledMultiSelectProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { value, name, onChange } }) => {
        // ensure value is always an array
        const selectedOptions = Array.isArray(value) ? value : [];
        return (
          <MultiSelect
            name={name}
            placeholder={placeholder}
            label={label}
            readOnly={readOnly}
            value={selectedOptions}
            options={options || []}
            disableClearAll={disableClearAll}
            disableRemoveCondition={disableRemoveCondition}
            widthVariant={widthVariant}
            onChange={(options: OptionType[]) => {
              onChange(options);
              onSave(options);
            }}
            errors={errors}
            required={required}
          />
        );
      }}
    />
  );
};

export default ControlledMultiSelect;
